// Which of the Orders table's 30 columns are visible, and how that choice persists.
//
// WHY THIS EXISTS: the table renders 30 columns in a ~2289px table inside a ~1247px
// window — 54% visible — with Status at position 27 and Action at 30. Reaching the two
// columns ops actually act on scrolls Order ID and Patient Name off screen entirely, so
// you end up changing a status on a row you can no longer identify. Ten columns are
// enough for the daily loop (identity -> state -> who to call -> when -> money -> what
// kind -> act); the other twenty stay one click away rather than being deleted.
//
// IDs BELOW ARE REACT-TABLE COLUMN IDs, NOT HEADERS. react-table derives a column's id
// from its `accessor` string unless an explicit `id` is set — hence
// "mapUserPatient.Patient.name" for Name and "caregiverUser" for Caregiver (the only
// column in ORDERS_COLUMN that sets `id` explicitly). Getting one wrong doesn't throw;
// the column simply never hides, so keep these in step with
// src/components/orderTable/Constants.js and ./columns.jsx.
//
// The `#` column is deliberately absent from every group: its accessor is `""`, so it
// has no usable react-table id. Rather than edit the shared legacy Constants.js (which
// /all-orders also renders) to give it one, it is treated as permanently visible.

/** Visible when the user has never chosen. Ten columns; see the header note for why. */
export const DEFAULT_VISIBLE = [
    "id",
    "mapUserPatient.Patient.name",
    "status",
    "contact",
    "createdAt",
    "endDate",
    "total",
    "type",
    "packedByName",
    "action",
];

/**
 * Every hideable column, grouped for the picker. Order within a group is the order the
 * checkboxes render in; group order is the order the sections render in.
 *
 * Every id here must also exist in the rendered column set, and vice versa — a column
 * missing from this file can never be hidden, which is the safe failure direction.
 */
export const COLUMN_GROUPS = [
    {
        label: "Identity",
        columns: [
            { id: "id", label: "ID" },
            { id: "mapUserPatient.Patient.name", label: "Name" },
            // Renders the same string as Name whenever the order's relationship is
            // SELF (User and Patient are then the same person), which is why it is
            // off by default rather than sitting beside its own duplicate.
            { id: "caregiverUser", label: "Caregiver" },
            { id: "contact", label: "Contact" },
        ],
    },
    {
        label: "Status & fulfilment",
        columns: [
            { id: "status", label: "Status" },
            { id: "type", label: "Type" },
            { id: "deliveryDetails", label: "Delivery Details" },
            { id: "packedByName", label: "Packed By" },
            { id: "drxView", label: "DRX View" },
            { id: "orderNotes", label: "Order Notes" },
            { id: "action", label: "Action" },
        ],
    },
    {
        label: "Dates",
        columns: [
            { id: "createdAt", label: "Order Date" },
            { id: "startDate", label: "Start Date" },
            { id: "endDate", label: "EndDate" },
            { id: "noOfDays", label: "Duration" },
            // `daysLeft` is computed server-side by the futureOrders endpoint only —
            // GET /userOrder/admin does not return it, so this column renders blank
            // here. Listed so it can be switched on, not hidden away as if missing.
            { id: "daysLeft", label: "Left (not returned by this endpoint)" },
        ],
    },
    {
        label: "Money",
        columns: [
            { id: "total", label: "Total Amount (₹)" },
            { id: "amountReceived", label: "Amount Received" },
            { id: "outstanding", label: "Refund / Pending" },
            { id: "evitalMedicineAmount", label: "Evital Med Amt (₹)" },
            { id: "serviceChargeAmount", label: "Service Charge (₹)" },
            { id: "inventoryId", label: "Inventory No" },
            { id: "evitalBill", label: "Bill" },
        ],
    },
    {
        label: "Address",
        columns: [
            { id: "completeAddress", label: "Complete Address" },
            { id: "city", label: "Order City" },
            { id: "address.state", label: "State" },
            { id: "pincode", label: "Pincode" },
        ],
    },
    {
        label: "Attribution",
        columns: [
            { id: "partnerName", label: "Partner" },
            { id: "channel", label: "Channel" },
            { id: "source", label: "Source" },
        ],
    },
];

/** Every hideable id, flattened — the universe `hiddenColumns` is computed against. */
export const ALL_HIDEABLE_IDS = COLUMN_GROUPS.flatMap((group) => group.columns.map((column) => column.id));

// Bump when DEFAULT_VISIBLE changes. A stored preference from an older version is
// discarded rather than migrated, so a user who never touched the picker always gets
// the current default instead of being pinned to a set chosen for an older table.
const STORAGE_KEY = "pillup.ops.orders.columns";
const STORAGE_VERSION = 2;

/** Ids hidden under the default — i.e. everything hideable that isn't in DEFAULT_VISIBLE. */
export function defaultHiddenColumns() {
    return ALL_HIDEABLE_IDS.filter((id) => !DEFAULT_VISIBLE.includes(id));
}

/**
 * The user's stored hidden-column ids, or the default set.
 *
 * Never throws: localStorage can be unavailable (private mode, disabled storage) and the
 * stored value can be malformed. A column preference is not worth failing a page render
 * over, so every failure path falls back to the default.
 */
export function loadHiddenColumns() {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return defaultHiddenColumns();
        const parsed = JSON.parse(raw);
        if (!parsed || parsed.version !== STORAGE_VERSION || !Array.isArray(parsed.hidden)) {
            return defaultHiddenColumns();
        }
        // Drop ids that no longer exist, so a renamed or removed column can't leave a
        // stale entry that silently hides nothing.
        return parsed.hidden.filter((id) => ALL_HIDEABLE_IDS.includes(id));
    } catch (error) {
        return defaultHiddenColumns();
    }
}

/** Persist the hidden-column ids. Silently no-ops if storage is unavailable. */
export function saveHiddenColumns(hidden) {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: STORAGE_VERSION, hidden }));
    } catch (error) {
        // Ignored on purpose — the choice still applies for this session.
    }
}
